import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Colors from "../../styles/Colors";
import { H1, H2, H4 } from "../../styles/Fonts";
import { SmallRegular } from "../../styles/Fonts";
import ActionCol from "./ActionCol";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import BackTestChart from "./BacktestChart";
import { openNewTab } from "../../utils/common";
import LabelWithHint from "../../components/LabelWithHint";
import useUserAutoVaultState from "../../hooks/useUserAutoVaultState";
import useAutoVaultState from "../../hooks/useAutoVaultState";
import { motion } from "framer-motion";
import { ALL_FARMS, FarmProps } from "../../utils/constant/farm";
import { useAppSelector } from "../../hooks";
import { SupportedChainId } from "../../utils/constant/chains";

const bulletText = [
  "The simulation is based on historical Uniswap V3 swap data on Arbitrum (WETH-USDC 0.05% pool). We simulate the resulted performance of which the strategy would have performed if we had provided liquidity in the backtest period",
  "Rebalances were done every 5 days",
  "Stop Loss was triggered by our algorithm when volatility exceeded the volatility threshold",
  "Swap Fees, Borrowing PnL, Borrowing Interests are incurred at every rebalance",
  "Initial Investment = $10,000",
  "Backtest period: 2021/11/20 - 2022/8/20",
];

const metrics = [
  { label: "ROI (%):", value: "84.19%" },
  { label: "APR (%):", value: "113.81%" },
  { label: "Sharpe Ratio:", value: "3.603" },
  { label: "Maximum Drawdown:", value: "-13.62%" },
];

const defaultChainId = SupportedChainId.ARBITRUM_ONE;

const AutoVaultPage: React.FC = () => {
  const selectedChainId = useAppSelector(
    (state) => state.account.selectedChainId
  );

  const [farm, setFarm] = useState<FarmProps>(
    ALL_FARMS[selectedChainId || defaultChainId]
  );

  const {
    updateAutoVaultState,
    totalSupply,
    priceRange,
    apr,
    cumulativeFee,
    vaultState,
  } = useAutoVaultState(
    farm.factoryAddress,
    farm.farmAddress,
    farm.farmActionAddress,
    farm.tokenDecimal,
    farm.deployedChainId
  );

  const {
    updateUserAutoVaultState,
    positionValue,
    canWithdrawAmount,
    registeredDepositAmount,
    registeredWithdrawAmount,
  } = useUserAutoVaultState(farm.farmAddress, farm.tokenDecimal);

  useEffect(() => {
    setFarm(ALL_FARMS[selectedChainId]);
  }, [selectedChainId]);

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <MainContainer>
        <RowContainer>
          <LeftContainer>
            <Title>Automated Vault</Title>
            <BackgroundContainer>
              <TextContainer>
                <VaultTitle>Market Neutral Vault</VaultTitle>
                <DescriptionText>ETH/USDC Uniswap V3</DescriptionText>
              </TextContainer>
              <DetailContainer>
                <DetailCol>
                  <Label>TVL</Label>
                  <Value>
                    {`${totalSupply ? (+totalSupply).toLocaleString() : "-"}
                     ${farm.tokenSymbol}`}
                  </Value>
                </DetailCol>
                <DetailCol>
                  <Label>Cumulative Fee</Label>
                  <Value>
                    {`${cumulativeFee ? (+cumulativeFee).toLocaleString() : "-"}
                     ${farm.tokenSymbol}`}
                  </Value>
                </DetailCol>
                <DetailCol>
                  <Label>APR</Label>
                  <Value>{`${apr.toFixed(2)} %`}</Value>
                </DetailCol>
                <DetailCol>
                  <Label>Range</Label>
                  <Value>
                    {priceRange
                      ? `$${priceRange.lowerTickPrice.toFixed(
                          2
                        )} - $${priceRange.upperTickPrice.toFixed(2)}`
                      : "-"}
                  </Value>
                </DetailCol>
              </DetailContainer>
            </BackgroundContainer>
            <MobileActionContainer>
              <ActionCol
                updateAccountFarmStatus={updateUserAutoVaultState}
                positionValue={positionValue}
                canWithdrawAmount={canWithdrawAmount}
                registeredDepositAmount={registeredDepositAmount}
                registeredWithdrawAmount={registeredWithdrawAmount}
                updateAutoVaultState={updateAutoVaultState}
                vaultState={vaultState}
                farm={farm}
              />
            </MobileActionContainer>
            <BackgroundContainer>
              <Subtitle>Introduction</Subtitle>
              <Introduction>
                The Crab Vault provides liquidity in Uniswap v3 using investors’
                USDC and the ETH borrowed from Diamond Pool. It is a market
                neutral vault that performs best in sideways markets where price
                barely moves during the rebalance period.
              </Introduction>
            </BackgroundContainer>
            <BackgroundContainer>
              <Subtitle>Vault Parameters</Subtitle>
              <ParamContainer>
                <ParamRow>
                  <ParamLabel>Range</ParamLabel>
                  <ParamValue>±15% based on current price</ParamValue>
                </ParamRow>
                <ParamRow>
                  <ParamLabel>Leverage Ratio</ParamLabel>
                  <ParamValue>2X</ParamValue>
                </ParamRow>
                <ParamRow>
                  <ParamLabel>Rebalance Period</ParamLabel>
                  <ParamValue>5 Days</ParamValue>
                </ParamRow>
                <ParamRow>
                  <ParamLabel>Stop Loss</ParamLabel>
                  <LabelWithHint
                    labelComponent={<ParamValue>Yes</ParamValue>}
                    hintText="Stop loss is triggered when the price and the implied
                    volatility rises in an accelerating rate. It is controlled
                    by our algorithm"
                  />
                </ParamRow>
                <ParamRow>
                  <ParamLabel>Liquidation Threshold</ParamLabel>
                  <Row>
                    <ParamValue>80%</ParamValue>
                    <HintIcon
                      icon={faInfoCircle}
                      onClick={() =>
                        openNewTab(
                          "https://dmo-protocol.gitbook.io/diamond/unibot/pool#risk-parameters"
                        )
                      }
                    />
                  </Row>
                </ParamRow>
              </ParamContainer>
            </BackgroundContainer>
            <BackgroundContainer>
              <Subtitle>Backtesting Result</Subtitle>
              <SectionTitle>Methodology</SectionTitle>
              {bulletText.map((text, index) => (
                <BulletContainer key={index}>
                  <BulletPoint>
                    <FontAwesomeIcon icon={faCircle} fontSize={6} />
                  </BulletPoint>
                  <BulletText>{text}</BulletText>
                </BulletContainer>
              ))}
              <SectionTitle>Key Metrics</SectionTitle>
              <ParamContainer>
                {metrics.map((metric) => (
                  <ParamRow key={metric.label}>
                    <ParamLabel>{metric.label}</ParamLabel>
                    <ParamValue>{metric.value}</ParamValue>
                  </ParamRow>
                ))}
              </ParamContainer>
              <SectionTitle>
                Backtest Result (WETH-USDC Crab Vault)
              </SectionTitle>
              <ChartContainer>
                <BackTestChart />
              </ChartContainer>
            </BackgroundContainer>
          </LeftContainer>
          <RightContainer>
            <ActionCol
              updateAccountFarmStatus={updateUserAutoVaultState}
              positionValue={positionValue}
              canWithdrawAmount={canWithdrawAmount}
              registeredDepositAmount={registeredDepositAmount}
              registeredWithdrawAmount={registeredWithdrawAmount}
              updateAutoVaultState={updateAutoVaultState}
              vaultState={vaultState}
              farm={farm}
            />
          </RightContainer>
        </RowContainer>
      </MainContainer>
    </Container>
  );
};

export default AutoVaultPage;
const Container = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: auto;
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const RowContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 20px;
  overflow: hidden;
  @media (max-width: 992px) {
    display: flex;
    flex-direction: column;
  }
`;

const LeftContainer = styled.div`
  flex: 1 1 400px;
  @media (max-width: 992px) {
    flex: initial;
    padding: 0;
    background-color: transparent;
  }
  @media (max-width: 768px) {
    flex: initial;
  }
`;

const RightContainer = styled.div`
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  @media (max-width: 992px) {
    flex: initial;
    display: none;
  }
`;

const Title = styled(H1)`
  font-size: 36px;
  line-height: 44px;
  margin-bottom: 28px;
  text-align: left;
  color: ${Colors.gray1};
  font-family: TT Firs Neue, Goldman-Sans, Helvetica Neue, Helvetica, Roboto,
    PingFang TC, 微軟雅黑, Microsoft Yahei, sans-serif;
  @media (max-width: 576px) {
    font-size: 28px;
    line-height: 36px;
    margin-bottom: 20px;
  }
`;

const VaultTitle = styled(H2)`
  font-size: 24px;
  line-height: 36px;
  text-align: left;
  color: ${Colors.gray1};
  font-family: TT Firs Neue, Goldman-Sans, Helvetica Neue, Helvetica, Roboto,
    PingFang TC, 微軟雅黑, Microsoft Yahei, sans-serif;
  @media (max-width: 576px) {
    font-size: 16px;
    line-height: 28px;
  }
`;

const DescriptionText = styled(H4)`
  font-weight: bold;
  color: ${Colors.ivory};
  margin-top: 8px;
  font-size: 14px;
  line-height: 24px;
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 22px;
  }
`;

const BackgroundContainer = styled.div`
  margin-top: 20px;
  background-color: ${Colors.backgroundGray2};
  padding: 24px;
  border-radius: 8px;
`;

const TextContainer = styled.div`
  flex: 1 1 200px;
  @media (max-width: 768px) {
    flex: inherit;
  }
`;

const DetailContainer = styled.div`
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  @media (max-width: 576px) {
    display: block;
    gap: 0px;
    background-color: ${Colors.backgroundGray4};
    padding: 12px 0;
    border-radius: 4px;
  }
`;

const DetailCol = styled.div`
  flex: 1 0 160px;
  border-radius: 4px;
  padding: 12px 20px;
  background-color: ${Colors.backgroundGray4};
  @media (max-width: 576px) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-radius: 0px;
    padding: 2px 12px;
    background-color: transparent;
  }
`;

const Label = styled(SmallRegular)`
  display: block;
  color: ${Colors.ivory};
  margin-bottom: 4px;
  font-size: 13px;
  line-height: 20px;
  @media (max-width: 576px) {
    margin-bottom: 0px;
    font-size: 12px;
    line-height: 22px;
  }
`;

const Value = styled(SmallRegular)`
  display: block;
  color: ${Colors.ivory};
  font-size: 14px;
  line-height: 22px;
  font-weight: bold;
  @media (max-width: 576px) {
    font-size: 12px;
    line-height: 22px;
  }
`;

const Subtitle = styled(VaultTitle)`
  margin-bottom: 16px;
  font-size: 18px;
  line-height: 24px;
`;

const Introduction = styled(SmallRegular)`
  display: block;
  color: ${Colors.ivory};
  font-size: 14px;
  line-height: 22px;
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const ParamRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 8px;
  @media (max-width: 768px) {
    margin-top: 4px;
  }
`;

const ParamLabel = styled(SmallRegular)`
  flex: 0 0 200px;
  display: block;
  color: ${Colors.ivory};
  font-size: 14px;
  line-height: 24px;
  font-weight: normal;
  @media (max-width: 768px) {
    flex: 1;
    font-size: 12px;
    line-height: 22px;
  }
`;

const ParamValue = styled(ParamLabel)`
  flex: 1;
  font-weight: bold;
  text-align: right;
`;

const ParamContainer = styled.div`
  max-width: 400px;
  @media (max-width: 576px) {
    max-width: none;
  }
`;

const SectionTitle = styled(Subtitle)`
  font-size: 15px;
  margin-top: 24px;
  margin-bottom: 0px;
`;

const BulletContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 8px;
  @media (max-width: 768px) {
    margin-top: 4px;
  }
`;

const BulletPoint = styled.div`
  flex: 0 0 20px;
  color: ${Colors.ivory};
  height: 24px;
  display: flex;
  align-items: center;
`;

const BulletText = styled(DescriptionText)`
  flex: 1;
  margin: 0;
  font-weight: normal;
`;

const ChartContainer = styled.div`
  width: 99%;
  margin-top: 12px;
  padding: 32px 20px;
  position: relative;
  background-color: ${Colors.backgroundGray4};
  border-radius: 4px;
  position: relative;
`;

const MobileActionContainer = styled.div`
  display: none;
  @media (max-width: 992px) {
    display: block;
    margin-top: 20px;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const HintIcon = styled(FontAwesomeIcon)`
  display: inline;
  color: ${Colors.ivory};
  font-size: 12px;
  margin: auto 2px auto 6px;
  opacity: 0.8;
  cursor: pointer;
  :hover {
    opacity: 1;
  }
  @media (max-width: 576px) {
    margin: auto 2px auto 6px;
    font-size: 11px;
  }
`;
